import React, {useMemo, useState, useEffect} from 'react'
import * as PropTypes from 'prop-types'
import {isEmpty} from 'ramda'
import {createNonce} from '@modifi/utilities'
import {TextWrapper, HighLightText} from './styles'

const HighLighter = ({
  children,
  setHighlights,
  getHighlight,
  highlights,
  registerHighlighter,
  unregisterHighlighter,
  ...props
}) => {
  const [id] = useState(createNonce())
  const [highlightProps, setHighlightProps] = useState(null)
  // register highlighter
  useEffect(() => {
    if (registerHighlighter) {
      registerHighlighter(id)
    }
    if (unregisterHighlighter) {
      return () => unregisterHighlighter(id)
    }
    return undefined
  }, [id, registerHighlighter, unregisterHighlighter])
  // set highlights
  useEffect(() => {
    if (setHighlights) {
      setHighlights(children, id)
    }
  }, [children, id, setHighlights])
  // get highlight
  useEffect(() => {
    if (getHighlight) {
      setHighlightProps(getHighlight(highlights, id))
    }
  }, [getHighlight, highlights, id])

  return useMemo(() => {
    if (!highlightProps || isEmpty(highlightProps)) {
      return <TextWrapper {...props}>{children}</TextWrapper>
    }
    return (
      <TextWrapper
        data-highlighted-start={highlightProps?.start}
        data-highlighted-end={highlightProps?.end}
        {...props}
      >
        {children.substr(0, highlightProps?.start)}
        <HighLightText backgroundColor={highlightProps?.color}>
          {children.substr(highlightProps?.start, highlightProps?.end - highlightProps?.start + 1)}
        </HighLightText>
        {children.substr(highlightProps?.end + 1)}
      </TextWrapper>
    )
    // excluded ...props from deps since it will only pass data attributes for now
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, highlightProps])
}

HighLighter.propTypes = {
  children: PropTypes.string,
  setHighlights: PropTypes.func,
  getHighlight: PropTypes.func,
  highlights: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      start: PropTypes.number,
      end: PropTypes.number,
    })
  ),
  registerHighlighter: PropTypes.func,
  unregisterHighlighter: PropTypes.func,
}

HighLighter.defaultProps = {
  children: null,
  highlights: null,
  setHighlights: null,
  getHighlight: null,
  registerHighlighter: null,
  unregisterHighlighter: null,
}

export default HighLighter
